/**
 * TTS (Text-to-Speech) 関連の型定義
 */

export type TTSProvider = 'google' | 'elevenlabs' | 'gemini' | 'fishaudio'
export type TTSLanguage = 'ja-JP' | 'en-US' | 'en-GB' | 'ko-KR' | 'zh-CN'
export type VoiceGender = 'male' | 'female' | 'neutral'
export type VoiceTone = 'warm' | 'clear' | 'energetic' | 'calm' | 'professional' | 'friendly'
export type VoiceOverflowMode = 'fadeout' | 'extend'

export interface VoiceMetadata {
  id: string                    // ユニークID (例: 'ja-chirp3-zephyr')
  provider: TTSProvider
  voiceId: string               // APIに渡すボイスID (例: 'ja-JP-Chirp3-HD-Zephyr')
  name: string                  // 表示名 (例: 'Zephyr')
  language: TTSLanguage
  gender: VoiceGender
  tone: VoiceTone[]             // 複数トーン可
  tags: string[]                // 'narrator', 'commercial', 'casual', etc.
  modelId?: string              // ElevenLabs用 model_id
  speakingRate?: number         // デフォルト話速 (0.25-4.0)
  pitch?: number                // デフォルトピッチ (Google Non-Chirp3 TTSのみ)
  description: string           // LLM選択用の説明
  recommended?: boolean         // おすすめ声（UIで上位表示＋おすすめバッジ）。喋る系のリアルな声を優先。
}

export interface TTSSceneAudio {
  sceneKey: string              // 'scene1', 'scene2', etc.
  text: string                  // 読み上げテキスト
  voiceId: string               // 使用したボイスID (VoiceMetadata.id)
  audioPath: string             // プロジェクト相対パス ('tts/{projectId}/{hash}.mp3')
  audioDurationSec: number      // 音声の実際の長さ（秒）
  textHash: string              // テキストのSHA-256ハッシュ（キャッシュキー）
}

/**
 * テキスト候補キー（"headline_0", "selling_1" 等）に対応する音声参照
 * 同じテキスト+ボイスなら同じファイルを共有（sceneKey に依存しない）
 */
export interface TTSRef {
  audioPath: string         // "tts/{projectId}/{textHash}.mp3"（sceneKey なし）
  textHash: string          // hash(ttsText + voiceId) — 生成時のテキストのハッシュ
  voiceId: string           // 生成時のボイスID
  audioDurationSec: number
}

export interface TTSAudioPack {
  provider: TTSProvider
  voiceId: string               // 選択されたボイスのID
  voiceReasoning: string        // LLMによるボイス選択理由
  refs?: Record<string, TTSRef> // 新フォーマット: "headline_0" → TTSRef（optional）
  scenes: TTSSceneAudio[]       // 旧フォーマット維持（後方互換・他の消費者が依存）
  totalCostJpy: number          // TTS生成コスト（円）
  generatedAt: string           // 生成日時
  generatedVoiceIds: string[]   // 過去に音声生成したボイスID一覧（履歴）
  // brandId → voiceId → キャッシュ済みブランド音声（ブランド変更時の再利用）
  brandAudioCache?: Record<string, Record<string, TTSRef & { nameReading: string }>>
}

export interface TTSGenerationRequest {
  text: string
  provider: TTSProvider
  voiceId: string               // VoiceMetadata.voiceId (APIに渡す実際のID)
  language: TTSLanguage
  modelId?: string              // ElevenLabs用 model_id / Gemini用 モデル名
  speakingRate?: number
  pitch?: number                // Google TTSのみ（Chirp3-HDは非対応）
  trailingSilenceSec?: number   // ElevenLabs: 末尾silenceパディング（秒）
  elevenlabsStability?: number  // ElevenLabs: stability (0.0-1.0)
  geminiStyle?: string          // Gemini: 演技/口調のスタイル指示（自然言語・任意）
}

export interface TTSGenerationResult {
  audioBuffer: Buffer
  durationSec: number           // ffprobeで測定した実際の長さ
  mimeType: string
  charCount: number
  costJpy: number
}

export interface TTSVoiceConfig {
  voiceId: string               // VoiceMetadata.id
  enabled: boolean              // AI選択候補に含めるか（デフォルト: true）
  speakingRate: number          // 話速 (0.5-2.0)
  pitch?: number                // ピッチ (Google TTS の non-Chirp3 のみ)
}

// TTS フェード（プチノイズ除去）のイージングカーブ
export type TTSFadeCurve = 'linear' | 'easeIn' | 'easeOut' | 'easeInOut'

// TTS フェード設定
export interface TTSFadeSettings {
  fadeIn: boolean             // フェードイン ON/OFF
  fadeInDuration: number      // フェードイン秒数 (0.01-0.5)
  fadeInCurve: TTSFadeCurve
  fadeOut: boolean            // フェードアウト ON/OFF
  fadeOutDuration: number     // フェードアウト秒数 (0.01-0.5)
  fadeOutCurve: TTSFadeCurve
}

export const DEFAULT_TTS_FADE: TTSFadeSettings = {
  fadeIn: false,
  fadeInDuration: 0.05,    // 50ms — プチノイズ除去に十分な長さ
  fadeInCurve: 'linear',
  fadeOut: false,
  fadeOutDuration: 0.05,
  fadeOutCurve: 'linear',
}

// Google TTS / ElevenLabs 生成後の音声クリーンアップ設定
// 末尾パディング（apad）は tts.trailingSilenceSec で一元管理
export interface TTSAudioCleanup {
  enabled: boolean              // ffmpegクリーンアップを有効にする
  // フィルター個別トグル（両方オン可）
  silenceRemove: boolean        // silenceremove（末尾無音除去）
  silenceThresholdDb: number    // 末尾無音除去の閾値 (dBFS, 例: -40)
  adeclick: boolean             // adeclick（クリックノイズ除去）
  adeclickThreshold: number     // 感度 1-100（default: 10, 低いほど積極的に除去）
  // 末尾フェードアウト（ffmpeg afade）— silenceremove で除去できないノイズ向け
  trailingFade: boolean               // 末尾フェードアウト ON/OFF
  trailingFadeDuration: number        // フェード時間（秒, 0.1-1.0, default: 0.3）
  // 詳細設定（管理者向け・グローバル設定タブから設定）
  silenceRemoveStopPeriods?: number   // 1=末尾のみ(推奨), -1=全体から除去 (default: 1)
  silenceRemoveStopDuration?: number  // 無音と判定する最小持続時間（秒, default: 0.1）
  adeclickWindowMs?: number           // 検出ウィンドウサイズ ms (10-100, default: 55)
  adeclickOverlapPct?: number         // オーバーラップ % (50-95, default: 75)
}

export const DEFAULT_TTS_AUDIO_CLEANUP: TTSAudioCleanup = {
  enabled: true,
  silenceRemove: false,
  silenceThresholdDb: -40,
  adeclick: true,
  adeclickThreshold: 85,
  trailingFade: false,
  trailingFadeDuration: 0.3,
}

export interface TTSSettings {
  enabled: boolean              // TTS機能の有効/無効
  speakBrandName: boolean       // ブランド名を音声で話す（デフォルト: true）
  provider: TTSProvider         // 'google' | 'elevenlabs'
  language: TTSLanguage         // 言語設定
  overflowMode: VoiceOverflowMode  // 'fadeout' | 'extend'
  defaultVolume: number         // TTS音量 (0.0-1.0)
  musicVolumeWithTTS: number    // TTS使用時の背景音楽音量 (0.0-1.0)
  voiceConfigs: TTSVoiceConfig[]   // ボイス別設定（未設定はデフォルト値）
  trailingSilenceSec?: number   // 音声末尾に追加する無音（秒, デフォルト: 0.35）ElevenLabs向け
  elevenlabsStability?: number  // ElevenLabs stability (0.0-1.0, デフォルト: 0.7)
  fade?: TTSFadeSettings        // フェードイン/アウト設定（プチノイズ除去）
  audioCleanup?: TTSAudioCleanup  // Google TTS / ElevenLabs 用：生成後にffmpegでadeclick+silenceremove+apadを実行
}

// Google Chirp3-HD / ElevenLabs ボイスのデフォルト設定（全てオフ）
// → 既定の AI ボイス選択候補は Gemini TTS のみ（第一選択を Gemini 3.1 Flash にするため）。
//   ユーザーは設定の「ナレーション」タブで Google / ElevenLabs を任意に有効化できる。
const NON_GEMINI_VOICES_DEFAULT_DISABLED: TTSVoiceConfig[] = [
  // Google Chirp3-HD
  'ja-chirp3-zephyr',
  'ja-chirp3-sulafat',
  'ja-chirp3-gacrux',
  'ja-chirp3-laomedeia',
  'ja-chirp3-kore',
  'ja-chirp3-callirrhoe',
  'ja-chirp3-alnilam',
  'ja-chirp3-schedar',
  'ja-chirp3-sadaltager',
  'ja-chirp3-sadachbia',
  'ja-chirp3-rasalgethi',
  'ja-chirp3-puck',
  'ja-chirp3-iapetus',
  'ja-chirp3-umbriel',
  // ElevenLabs Eleven v3
  'el-jessica',
  'el-rachel',
  'el-bella',
  'el-domi',
  'el-chris',
  'el-sam',
  'el-arnold',
  'el-antoni',
  // Fish Audio S2.1 Pro（既定オフ・任意で有効化して試用）
  'fish-genki-f',
  'fish-calm-f',
  'fish-fuuka-f',
  'fish-announcer-f',
  'fish-calm-m',
  'fish-narrator-m',
  'fish-deep-m',
  'fish-story-m',
].map(voiceId => ({ voiceId, enabled: false, speakingRate: 1.1 }))

export const DEFAULT_TTS_SETTINGS: TTSSettings = {
  enabled: true,
  speakBrandName: true,
  provider: 'gemini',
  language: 'ja-JP',
  overflowMode: 'extend',
  defaultVolume: 0.9,
  musicVolumeWithTTS: 0.3,
  voiceConfigs: NON_GEMINI_VOICES_DEFAULT_DISABLED,
  trailingSilenceSec: 0.35,
  elevenlabsStability: 0.5,
  fade: DEFAULT_TTS_FADE,
  audioCleanup: DEFAULT_TTS_AUDIO_CLEANUP,
}
